import { FC, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { API_ENDPOINT } from "../../../constants/appConstant";
import {
  isDisabilitiesContant,
  isLgbtqiaContant,
  participateCheckContant,
  returningToWorkContant,
} from "../../../constants/lgbtqia";
import { useNetwork } from "../../../hooks/useNetwork";
import { DropdownOptionType } from "../../../types";
import {
  ActiveFieldType,
  ApopProps,
  LgbtqiaPrefillDataType,
} from "../../../types/apop";
import { ActionButtonContainer } from "../common/ActionButtonContainer";
import { Events } from "../common/json/clevertapEvents";
import CheckBoxController from "./components/controllers/checkBoxController";
import DropDownController from "./components/controllers/dropDownController";
import RadioController from "./components/controllers/radioController";
import TextAreaController from "./components/controllers/textAreaController";
import { MsiteCloseIcon } from "./components/msiteCloseIcon";
import { getDropDownList, removeNullAndEmptyValues } from "./utils";

type LgbtqiaApopProps = {
  closeApop: () => void;
} & ApopProps;

const fetchDropDownList = async (
  fetchAPI: any,
  type: string,
  parentUuids?: string,
) => {
  const args = { fetchAPI, type, parentUuids };
  return await getDropDownList(args);
};

export const LgbtqiaApop: FC<LgbtqiaApopProps> = (props) => {
  const {
    closeApop,
    prefilledData,
    nextApopType,
    onCTA,
    toast,
    handleEventTracking,
  } = props;
  const {
    formState: { isValid, errors },
    control,
    watch,
    handleSubmit,
    setValue,
    resetField,
  } = useForm();
  const { disabilities, type, subType, detail, returningToWork } = watch();

  const { response: apiResponse, fetchData: fetchAPI } = useNetwork();
  const { fetchData: submitFetchApi } = useNetwork();

  const [autoFillActive, setAutoFillActive] = useState<boolean>(true);
  const [activeField, setActiveField] = useState<ActiveFieldType | null>(null);
  const [typeList, setTypeList] = useState<DropdownOptionType[]>([]);
  const [subTypeList, setSubTypeList] = useState<DropdownOptionType[]>([]);
  const [detailList, setDetailList] = useState<DropdownOptionType[]>([]);
  const [isFemale, setIsFemale] = useState<boolean>(false);
  const [isDropdownOptionLoading, setIsDropdownOptionLoading] =
    useState<boolean>(false);

  const resetFields = ({ key }: ActiveFieldType) => {
    switch (key) {
      case "disabilities":
        if (autoFillActive) {
          setActiveField({ key: "disabilities", value: disabilities });
        } else {
          resetField("type");
          resetField("subType");
          resetField("detail");
          resetField("description");
          setTypeList([]);
          setSubTypeList([]);
          setDetailList([]);
        }
        break;
      case "type":
        if (autoFillActive) {
          setActiveField({ key: "type", value: type ? type.id : null });
        } else {
          resetField("type");
          resetField("subType");
          resetField("detail");
          resetField("description");
          setSubTypeList([]);
          setDetailList([]);
          setActiveField({ key: "type" });
        }
        break;
      case "subType":
        if (autoFillActive) {
          setActiveField({
            key: "subType",
            value: subType ? subType.id : null,
          });
        } else {
          resetField("subType");
          resetField("detail");
          resetField("description");
          setDetailList([]);
          setActiveField({ key: "subType" });
        }
        break;
      case "detail":
        if (autoFillActive) {
          setActiveField({ key: "detail", value: detail ? detail.id : null });
        } else {
          resetField("detail");
          resetField("description");
          setActiveField({ key: "detail" });
        }
        break;
      default:
        break;
    }
  };

  const handleUserInput = (value: ActiveFieldType) => {
    setAutoFillActive(false);
    setActiveField(value);
  };

  const fetchData = async (currentField: ActiveFieldType | null) => {
    if (currentField?.key === "disabilities") {
      if (currentField.value === "no") {
        resetFields({ key: "disabilities" });
        return;
      } else if (currentField.value === "yes") {
        try {
          setIsDropdownOptionLoading(true);
          const typeList = await fetchDropDownList(fetchAPI, "DISABILITY");
          setTypeList(typeList);
          setIsDropdownOptionLoading(false);
          resetFields({ key: "type" });
        } catch (error) {
          return;
        }
      }
    } else if (currentField?.key === "type") {
      if (type !== null) {
        try {
          setIsDropdownOptionLoading(true);
          const subTypeList = await fetchDropDownList(
            fetchAPI,
            "DISABILITY_SUBTYPE",
            currentField.value,
          );
          setSubTypeList(subTypeList);
          setIsDropdownOptionLoading(false);
          resetFields({ key: "subType" });
        } catch (error) {
          return;
        }
      }
    } else if (currentField?.key === "subType") {
      if (subType !== null) {
        try {
          setIsDropdownOptionLoading(true);
          const detailList = await fetchDropDownList(
            fetchAPI,
            "DISABILITY_DETAIL",
            currentField.value,
          );
          setDetailList(detailList);
          setIsDropdownOptionLoading(false);
          resetFields({ key: "detail" });
        } catch (error) {
          return;
        }
      }
    }
  };

  useEffect(() => {
    const preData = prefilledData?.eventPrefill;
    if (preData) {
      const {
        gender,
        disabilityData,
        lgbtqStatus,
        disability,
        returningToWork,
      } = preData as LgbtqiaPrefillDataType;
      setValue(
        "lgbtqia",
        lgbtqStatus == null ? null : lgbtqStatus ? "yes" : "no",
      );
      setValue(
        "disabilities",
        disability == null ? null : disability ? "yes" : "no",
      );
      setValue("type", disabilityData?.type ?? null);
      setValue("subType", disabilityData?.subType ?? null);
      setValue("detail", disabilityData?.detail ?? null);
      setValue("description", disabilityData?.description ?? "");
      setValue("returningToWork", returningToWork ?? false);
      setValue("participateCheck", false);
      setIsFemale(gender?.text?.toLowerCase() === "female");
      if (disability) {
        setActiveField({
          key: "disabilities",
          value: disability ? "yes" : "no",
        });
      }
    }
  }, [prefilledData]);

  useEffect(() => {
    activeField?.value && fetchData(activeField);
  }, [activeField?.key, activeField?.value]);

  const showReturnToWork = useMemo(() => {
    if (!isFemale) return null;
    return (
      <CheckBoxController
        name="returningToWork"
        label="Returning to work"
        control={control}
        errors={errors}
        options={returningToWorkContant}
        isRequired={false}
        setIsUserInput={handleUserInput}
      />
    );
  }, [isFemale, returningToWork]);

  const onSubmit = async (data: any) => {
    const payload = {
      eventId: 1,
      triumphFlag: true,
      lgbtqStatus: data?.lgbtqia === "yes",
      differentlyAbled: data?.disabilities === "yes",
      disabilityData:
        data?.disabilities === "no"
          ? null
          : {
              type: data?.type,
              subType: data?.subType,
              detail: data?.detail,
              description: data?.description,
            },
      returningToWork: data.returningToWork ?? false,
      isParticipate: data.participateCheck,
    };

    handleEventTracking({
      eventType: "eventPush",
      eventName: Events["LGBTQ"].submitEvent["EVENTNAME"],
      eventProps: Events["LGBTQ"].submitEvent["EVENTOBJ"],
    });

    const updatedPayload = removeNullAndEmptyValues(payload);
    try {
      const res = await submitFetchApi(
        `${API_ENDPOINT().seekerProfile.lgbtqDisability}`,
        "POST",
        {},
        updatedPayload,
      );
      onCTA && onCTA("LGBTQ");
      if (res.data) {
        const nextAction = (res.data as any)?.nextActionRequired?.actionType;
        if (!nextAction) {
          nextApopType(
            nextAction ?? "",
            (res.data as any)?.nextActionRequired ?? null,
          );
        }
      } else {
        toast?.({
          showToast: true,
          text:
            (res.error as any)?.error?.message?.errorMessage ??
            "Something went wrong",
          type: "error",
        });
      }
    } catch (error) {
      toast?.({
        showToast: true,
        text: "An unexpected error occurred",
        type: "error",
      });
    }
  };

  return (
    <div className="l1-flex l1-flex-col l1-gap-7 max-md:l1-gap-5">
      <MsiteCloseIcon fetchAPI={fetchAPI} closeApop={closeApop} />
      <form id="lgbtqiaForm" onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="l1-mb-36 l1-flex l1-flex-col l1-justify-between l1-gap-4">
          <div className="l1-flex l1-flex-col l1-gap-4">
            <RadioController
              name="lgbtqia"
              label="Do you belong to LGBTQIA+ community?"
              control={control}
              errors={errors}
              options={isLgbtqiaContant}
              isRequired={true}
              setIsUserInput={handleUserInput}
            />
            <RadioController
              name="disabilities"
              label="Do you identify as a person with disabilities?"
              control={control}
              errors={errors}
              options={isDisabilitiesContant}
              isRequired={true}
              setIsUserInput={handleUserInput}
            />
            {disabilities === "yes" && (
              <>
                <DropDownController
                  name="type"
                  label="Type"
                  control={control}
                  errors={errors}
                  options={typeList}
                  isRequired={true}
                  setIsUserInput={handleUserInput}
                />
                <DropDownController
                  name="subType"
                  label="Sub Type"
                  control={control}
                  errors={errors}
                  options={subTypeList}
                  isRequired={true}
                  setIsUserInput={handleUserInput}
                />
                {(detailList || []).length > 0 && (
                  <DropDownController
                    name="detail"
                    label="Detail"
                    control={control}
                    errors={errors}
                    options={detailList}
                    isRequired={true}
                    setIsUserInput={handleUserInput}
                  />
                )}
                <TextAreaController
                  name="description"
                  label="Description"
                  control={control}
                  errors={errors}
                  subType={subType}
                  setIsUserInput={handleUserInput}
                />
              </>
            )}
            {showReturnToWork}
          </div>
          <div className="l1-fixed l1-bottom-[3.8rem] l1-flex l1-w-full l1-flex-col l1-gap-2 l1-bg-white l1-pb-5 l1-pr-4 l1-pt-2">
            <CheckBoxController
              name="participateCheck"
              label="Participate Check"
              control={control}
              errors={errors}
              options={participateCheckContant}
              isRequired={true}
              setIsUserInput={handleUserInput}
            />
          </div>
        </div>
        <ActionButtonContainer
          primaryBtnLabel="Save"
          isLoading={apiResponse.isLoading ? true : false}
          disabled={!isValid}
        />
      </form>
    </div>
  );
};
